import * as React from 'react';
import { ActivityFeedViewV1 } from './ActivityFeedViewV1';
import { ActivityFeedItem } from '../service/ActivityFeedItem';
import { getActivityFeedStore } from '../store/store';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { loadActivityFeed } from '../actions/loadActivityFeed';
import { activityFeedItemAction } from '../actions/activityFeedItemAction';
import { observer } from 'mobx-react';
import { OpenMailAction } from './ActivityFeedActions';
import * as AdaptiveCards from 'adaptivecards';
import { logUsage } from 'owa-analytics';
import { getGuid } from 'owa-guid';
import { ActivityType } from '../service/ActivityFeedStorageItem';
import { dismissAllItems } from '../actions/dismissAllItems';
import { toggleActivityFeed } from '../actions/toggleActivityFeed';
import { getQueryStringParameter } from 'owa-querystring';
import { trace } from 'owa-trace';

declare var outlook;
export interface ActivityFeedProps {
    actions: OpenMailAction;
}
type MetricSummary = { [key in ActivityType]?: number } & {
    sessionId: string;
    unseen: number;
    total: number;
};

@observer
export class ActivityFeed extends React.Component<ActivityFeedProps, {}> {
    // session is defined as the span of time between this component is loaded and unloaded
    private sessionId: string;

    componentDidMount() {
        this.sessionId = getGuid();
        toggleActivityFeed(true);
        const items = [...getActivityFeedStore().items.values()];

        logUsage('ActivityFeed_Loaded', this.createSummary(items));
        loadActivityFeed();
    }

    componentWillUnmount() {
        toggleActivityFeed(false);
        logUsage('ActivityFeed_Unloaded', { sessionId: this.sessionId });
    }

    private createSummary(items: ActivityFeedItem[]): MetricSummary {
        let summary: MetricSummary = { sessionId: this.sessionId, unseen: 0, total: items.length };
        items.forEach(item => {
            const key = item.type;
            if (!(key in summary)) {
                summary[key] = 0;
            }
            summary[key] += 1;
            summary.unseen += item.isSeen ? 0 : 1;
        });

        return summary;
    }

    private sendPostMessage = (itemId: string, action: AdaptiveCards.Action) => {
        if (typeof outlook !== 'undefined') {
            // Send postMessage to Outlook Desktop
            // NotificationType currently only supports opening emails, but this will not be hit in production regardless.
            outlook.notifyOutlook(
                outlook.NotificationType.LikedMessageClicked,
                action.getJsonTypeName() + ' ' + (action as any).messageId
            );
        } else {
            trace.warn('Message not sent to Outlook Desktop');
            // Send postMessage to web platform
            try {
                window.parent.postMessage(
                    [action.getJsonTypeName(), (action as any).messageId],
                    '*'
                );
            } catch (ex) {
                trace.warn('Fail to send message to web platform: ' + ex);
                // if postMessage was not sent, perform the default action
                this.executeAction(itemId, action);
            }
        }
    };

    private executeAction = (itemId: string, action: AdaptiveCards.Action) => {
        // AdaptiveCard has a set of predefined action that we are "intercepting" and replacing with custom behaviors.
        // If we want to use one of the AdaptiveCard action we need to add a case here and define how to handle it.
        // If we want to implement a fully custom action use Action.OpenMail as example.
        switch (action.getJsonTypeName()) {
            case 'Action.OpenMail':
                this.props.actions.openMail((action as any).messageId);
                break;

            case 'Action.OpenUrl':
                window.open((action as any).url, '_blank');
                break;

            default:
                break;
        }
    };

    private handleAction = (itemId: string, action: AdaptiveCards.Action) => {
        activityFeedItemAction(itemId);
        const item: ActivityFeedItem = getActivityFeedStore().items.get(itemId);

        logUsage('ActivityFeed_ActionClicked', {
            sessionId: this.sessionId,
            action: action.getJsonTypeName(),
            itemId: itemId,
            itemType: item.type,
            itemRead: item.isRead, // was the item already read
            itemSeen: item.isSeen, // was the item already shown
        });

        let defaultActionsParamName = 'default';
        let defaultValue = 'true';
        let useDefaultActions = getQueryStringParameter(defaultActionsParamName);
        if (useDefaultActions == null) {
            useDefaultActions = defaultValue;
        }
        let useDefaultBool = useDefaultActions == 'true';

        if (useDefaultBool) {
            this.executeAction(itemId, action);
        } else {
            this.sendPostMessage(itemId, action);
        }
    };

    render() {
        const { firstFetchComplete, items } = getActivityFeedStore();
        return firstFetchComplete ? (
            <ActivityFeedViewV1
                cards={[...items.values()]}
                onAdaptiveCardAction={this.handleAction}
                onDismissAll={dismissAllItems}
            />
        ) : (
            <Spinner size={SpinnerSize.large} />
        );
    }
}
